/**
 * Keep storefront dir/lang stable while navigating between storefront pages —
 * both `/store/{id}` on the platform host and the bare paths a store serves on
 * its own host. Only restore the app language when leaving the storefront,
 * and only after the destination page is on screen.
 */
import {
  applyLanguageToDocument,
  ensureKawadernaMessages,
  isKawadernaLang,
  useLocaleNavHold,
} from '~/composables/useKawadernaI18n'
import { storefrontStoreIdForPath } from '~/composables/useStorefrontHost'
import {
  normalizeStoreLanguage,
  useStorefrontLangLocked,
  useStorefrontLangOverride,
} from '~/composables/storefront/langState'

export default defineNuxtPlugin((nuxtApp) => {
  const route = useRoute()
  const override = useStorefrontLangOverride()
  const locked = useStorefrontLangLocked()
  const hold = useLocaleNavHold()
  const { lang: appLang } = useKawadernaI18n()
  const storefrontHost = useStorefrontHost()

  // Loaded on first storefront route only — a static reference here put the
  // store editor (~400 KB) in the entry chunk of every platform page.
  type PeekById = ReturnType<typeof import('~/composables/useOnlineStores').useOnlineStores>['peekById']
  let peekById: PeekById | null = null
  let storesLoading: Promise<void> | null = null

  function loadStores(): Promise<void> {
    storesLoading ??= import('~/composables/useOnlineStores').then((mod) => {
      peekById = nuxtApp.runWithContext(() => mod.useOnlineStores().peekById)
    })
    return storesLoading
  }

  function storeIdFor(path: string): string | null {
    return storefrontStoreIdForPath(storefrontHost.value, path)
  }

  function syncFromRoute(hydrating = nuxtApp.isHydrating) {
    // On the store's own host the storefront answers at the root, so matching
    // `/store/{id}` alone would read every page there as "left the storefront"
    // and drop the whole shop back to the visitor's app language.
    const storeId = storeIdFor(route.path)
    if (!storeId) {
      if (override.value != null || locked.value) {
        override.value = null
        locked.value = false
        applyLanguageToDocument(appLang.value)
      }
      return
    }

    if (!peekById) {
      if (override.value) applyLanguageToDocument(override.value)
      void loadStores().then(() => syncFromRoute(hydrating))
      return
    }

    const store = peekById(storeId)
    if (!store) {
      // Still on storefront — keep the previous override during loading.
      if (override.value) applyLanguageToDocument(override.value)
      return
    }

    // Respect a language the shopper picked in the store menu.
    if (locked.value && isKawadernaLang(override.value)) {
      applyLanguageToDocument(override.value)
      return
    }

    const next = normalizeStoreLanguage(store.language)
    // Do not rewrite the payload language while Vue is hydrating — that is
    // what turned SSR English owner/shopper strings into Arabic on the client.
    if (hydrating && isKawadernaLang(override.value)) {
      applyLanguageToDocument(override.value)
      return
    }

    override.value = next
    applyLanguageToDocument(next)
    // Dictionaries are reactive, so labels repaint once this lands.
    void ensureKawadernaMessages(next)
  }

  watch(
    () => route.path,
    (path, previous) => {
      const now = Boolean(storeIdFor(path))
      const was = previous == null ? now : Boolean(storeIdFor(previous))
      // Route updates as soon as navigation starts, while the outgoing page
      // (dashboard or store) is still painted and often waiting on async setup.
      if (previous != null && now !== was) {
        hold.value = true
        return
      }
      syncFromRoute()
    },
    { immediate: true },
  )

  // `page:finish` runs before Vue unmounts the outgoing page. Applying dir
  // here reflows the store (or dashboard) that is still in the document.
  // Destination pages apply lang/dir from onMounted / onUnmounted instead.
  nuxtApp.hook('page:finish', () => {
    hold.value = false
  })
  nuxtApp.hook('app:error', () => {
    hold.value = false
  })
})
